<template>
  <div>
    <!-- Chat Window -->
    <div id="chat-popup" v-if="isChatVisible" class="chat-popup">
      <div class="chat-header">
        <h4>MDM Digital Assistant</h4>
        <button class="close-btn" @click="toggleChat">&times;</button>
      </div>
      <div class="chat-messages">
        <div
          v-for="(message, i) in messages"
          :key="i"
          class="chat-message"
          :class="{ 'user-message': message.isUser }"
          v-html="message.text"
        ></div>
      </div>
      <div class="chat-input">
        <input
          v-model="newMessage"
          @keyup.enter="sendMessage"
          placeholder="Type a message…"
        />
        <button class="send-btn" @click="sendMessage">Send</button>
      </div>
    </div>

    <!-- Chat Toggle Button -->
    <button
      v-else
      class="open-chat-btn"
      @click="toggleChat"
      aria-label="Open chat"
    >
      <img :src="chatIcon" alt="Chat Icon" class="chat-icon-img" />
    </button>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ChatPopup',
  data() {
    return {
      isChatVisible: false,
      messages: [
        { text: 'Hello! How can I help you today?', isUser: false }
      ],
      newMessage: ''
    }
  },
  computed: {
    chatIcon() {
      // if you put live-chat.png into src/assets/
      return require('@/assets/live-chat.png')
    }
  },
  methods: {
    toggleChat() {
      this.isChatVisible = !this.isChatVisible
    },
    async sendMessage() {
      // prevent empty
      if (!this.newMessage.trim()) return

      // push user message
      this.messages.push({ text: this.newMessage, isUser: true })
      const payload = this.newMessage
      this.newMessage = ''

      try {
        const resp = await axios.post(
          `/chat`,
          { message: payload },
          { timeout: 30000, headers: { 'X-API-Key': process.env.VUE_APP_CHAT_API_KEY } }
        )
        this.messages.push({
          text: resp.data.reply,
          isUser: false
        })
      } catch (err) {
        console.error('Chat request failed:', err)
        this.messages.push({
          text: 'Error: could not reach server.',
          isUser: false
        })
      }
    }
  }
}
</script>

<style scoped>
/* ─────────────────────────────────────────────────────────────────────────
   Container
───────────────────────────────────────────────────────────────────────── */
.chat-popup {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 400px;
  height: 600px;
  min-width: 250px;
  min-height: 300px;
  background-color: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  resize: both;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  transition: transform 0.2s ease;
}

/* Hover lift */
.chat-popup:hover {
  transform: translateY(-4px);
}

/* ─────────────────────────────────────────────────────────────────────────
   Header (draggable)
───────────────────────────────────────────────────────────────────────── */
.chat-header {
  background-color: #FDC300;
  color: #fff;
  padding: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: move; /* indicate draggable */
  user-select: none;
}

/* Title */
.chat-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

/* Close button */
.close-btn {
  background: none;
  border: none;
  color: #fff;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;
}
.close-btn:hover {
  color: #333;
}

/* ─────────────────────────────────────────────────────────────────────────
   Messages area
───────────────────────────────────────────────────────────────────────── */
.chat-messages {
  flex: 1;
  padding: 10px;
  overflow-y: auto;
  background-color: #f9f9f9;
  scroll-behavior: smooth;
}

/* Individual message */
.chat-message {
  margin-bottom: 10px;
  padding: 10px 14px;
  border-radius: 12px;
  max-width: 80%;
  font-size: 14px;
  line-height: 1.5;
  background-color: #f1f1f1;
  color: #333;
  white-space: normal;
  position: relative;
}

/* Timestamp (optional) */
.chat-message::after {
  content: attr(data-time);
  position: absolute;
  bottom: -14px;
  right: 10px;
  font-size: 10px;
  color: #888;
}

/* User message alignment & style */
.user-message {
  background-color: #FDC300;
  color: #fff;
  align-self: flex-end;
  font-weight: 500;
}

/* ─────────────────────────────────────────────────────────────────────────
   Input area
───────────────────────────────────────────────────────────────────────── */
.chat-input {
  display: flex;
  padding: 10px;
  border-top: 1px solid #ddd;
  background: #fff;
}

/* Text input */
.chat-input input {
  flex: 1;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s;
}
.chat-input input:focus {
  outline: none;
  border-color: #FDC300;
}

/* Send button */
.send-btn {
  margin-left: 8px;
  background-color: #FDC300;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s, transform 0.1s;
}
.send-btn:hover {
  background-color: #e5b200;
}
.send-btn:active {
  transform: scale(0.98);
}

/* ─────────────────────────────────────────────────────────────────────────
   Open button
───────────────────────────────────────────────────────────────────────── */
.open-chat-btn {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: #FDC300;
  border: none;
  border-radius: 50%;
  width: 56px;
  height: 56px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: background-color 0.2s, transform 0.2s;
}
.open-chat-btn:hover {
  background-color: #e5b200;
  transform: translateY(-2px);
}

/* Chat icon inside button */
.chat-icon-img {
  width: 28px;
  height: 28px;
}

/* ─────────────────────────────────────────────────────────────────────────
   Responsive tweaks
───────────────────────────────────────────────────────────────────────── */
@media (max-width: 480px) {
  .chat-popup {
    width: 90%;
    height: 60%;
    bottom: 10px;
    right: 5%;
  }
  .open-chat-btn {
    bottom: 10px;
    right: 5%;
    width: 48px;
    height: 48px;
  }
  .chat-icon-img {
    width: 24px;
    height: 24px;
  }
}
</style>
